import {gql} from '@apollo/client';
import type {GqlDownloadRequestPayload, GqlRequest} from '@igp/shared/api';
import {GqlRequestBuilder, type IDownloadByLinkRequestBuilder, maxServerDownloadPageSize} from '@igp/shared/api';
import type {QueryExportRepaymentLogsArgs, QueryGetRepaymentLogsArgs} from '@igp/shared/contract/pam';
import type {EntityType} from '@models';
import type {DocumentNode} from 'graphql';

import type {RepaymentLogDownloadFields, RepaymentLogFilterKeys, RepaymentLogQueryFields, RepaymentLogTextFilterKeys} from './types';

export interface IRepaymentLogRequestBuilder extends IDownloadByLinkRequestBuilder<QueryGetRepaymentLogsArgs, RepaymentLogDownloadFields> {
    getCreateMutation: () => DocumentNode;
    getEditMutation: () => DocumentNode;
    getRemoveMutation: () => DocumentNode;
}

export class RepaymentLogRequestBuilder
    extends GqlRequestBuilder<EntityType, QueryGetRepaymentLogsArgs, RepaymentLogQueryFields, RepaymentLogFilterKeys>
    implements IRepaymentLogRequestBuilder
{
    buildDownloadRequest(
        downloadRequestPayload: GqlDownloadRequestPayload<RepaymentLogDownloadFields>
    ): GqlRequest<QueryExportRepaymentLogsArgs> {
        const filter = this.mapToFilterObject(downloadRequestPayload.filter);
        const queryArgs = this.getGqlPageArgs(filter);
        return {
            query: this.buildExportQuery(),
            variables: {
                report_name: downloadRequestPayload.report_name,
                filter: queryArgs?.filter,
                sort: queryArgs?.sort,
                start: 0,
                end: maxServerDownloadPageSize,
                fields: downloadRequestPayload.columns.map(column => ({
                    column_name: column.name,
                    field: column.field,
                })),
            },
        };
    }

    protected validateFilters(_filter: Partial<Record<RepaymentLogFilterKeys, unknown>>): void {
        return;
    }
    protected buildQuery(fields: RepaymentLogQueryFields[]): DocumentNode {
        const requiredFileds: RepaymentLogQueryFields[] = ['repayment_id'];
        const queryFields = [...new Set([...requiredFileds, ...fields])];
        return gql`
            query GetRepaymentLogs($filter: RepaymentLogsFilter, $start: Int, $end: Int, $sort: Sorting) {
                getRepaymentLogs(filter: $filter, start: $start, end: $end, sort: $sort) {
                    items {
                        ${this.buildQueryFields(queryFields)}
                    }
                    total_count
                }
            }
        `;
    }
    protected buildFilter(filter: Partial<Record<RepaymentLogFilterKeys, unknown>>): QueryGetRepaymentLogsArgs {
        return {
            filter: {
                text: this.getGQLTextFilter(
                    Object.keys(this.filterFieldsMapper).map((key: RepaymentLogTextFilterKeys) =>
                        this.toGQLTextFilter(this.filterFieldsMapper[key], filter[key] as string)
                    )
                ),
                repayment_date: this.toGQLDateRange(filter['repayment_date.from'], filter['repayment_date.to']),
            },
        };
    }
    getCreateMutation(): DocumentNode {
        return gql`
            mutation CreateRepaymentLog($log: CreateRepaymentLogInput!) {
                createRepaymentLog(log: $log) {
                    repayment_id
                }
            }
        `;
    }
    getEditMutation(): DocumentNode {
        return gql`
            mutation EditRepaymentLog($log: EditRepaymentLogInput!) {
                editRepaymentLog(log: $log) {
                    repayment_id
                }
            }
        `;
    }
    getRemoveMutation(): DocumentNode {
        return gql`
            mutation RemoveRepaymentLog($repayment_id: String!) {
                removeRepaymentLog(repayment_id: $repayment_id)
            }
        `;
    }

    private readonly filterFieldsMapper: Record<RepaymentLogTextFilterKeys, RepaymentLogTextFilterKeys[]> = {
        from_agent_uid: ['from_agent_uid'],
        master_agent_uid: ['master_agent_uid'],
    };

    private buildExportQuery(): DocumentNode {
        return gql`
            query ExportRepaymentLogs(
                $filter: RepaymentLogsFilter
                $report_name: String!
                $fields: [ReportField]
                $sort: Sorting
                $start: Int
                $end: Int
            ) {
                exportRepaymentLogs(filter: $filter, report_name: $report_name, fields: $fields, sort: $sort, end: $end, start: $start) {
                    url
                }
            }
        `;
    }
}
